import { useQuery } from '@tanstack/react-query';

import { type ITeamSpendPosition, toSpendPosition } from '@/authz/team-spend';
import { coreApi } from '@/lib/api-client';

import { teamAccessKeys } from './team-access-keys';

interface IEnvelope<T> {
	message: string;
	statusCode: number;
	isSuccessful: boolean;
	data: T;
}

/**
 * A team member's allowance: `GET /api/v1/SpendAuthorization/me` (role `Delegate Support` only).
 *
 * Only ever enabled for a team member — anyone else gets a 403 by design. The position moves with
 * every send (the member's and their teammates'), so it is refetched on focus and kept short-lived.
 * A malformed answer is an error, not "can't spend": the caller must not invent a refusal.
 */
export const useTeamSpendPosition = (enabled: boolean) =>
	useQuery({
		queryKey: teamAccessKeys.spendPosition(),
		queryFn: async (): Promise<ITeamSpendPosition> => {
			const { data } = await coreApi.get<IEnvelope<unknown>>('/api/v1/SpendAuthorization/me');
			const position = toSpendPosition(data?.data);
			if (!position) throw new Error('The allowance answer was not readable.');
			return position;
		},
		enabled,
		staleTime: 15_000,
		refetchOnWindowFocus: true,
		retry: 1,
	});
